import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Syringe,
  AlertTriangle,
  Info,
  Calculator,
  Heart,
  Activity,
  Droplets,
  Eye,
  Shield
} from 'lucide-react';

export default function NoradrenalinePage() {
  const [patientType, setPatientType] = useState('adulte'); // adulte ou pediatrique
  const [poids, setPoids] = useState('');
  const [posologie, setPosologie] = useState('0.2'); // µg/kg/min
  const [debit, setDebit] = useState(null);

  // Calcul automatique du débit
  useEffect(() => {
    if (poids && posologie) {
      const p = parseFloat(poids);
      const pos = parseFloat(posologie);
      
      if (p > 0 && pos > 0) {
        // Formule : Débit (ml/h) = (Posologie µg/kg/min × Poids kg × 60) / (Concentration mg/ml × 1000)
        // Concentration = 0.2 mg/ml pour adulte
        // Débit (ml/h) = (pos × p × 60) / (0.2 × 1000) = (pos × p × 60) / 200 = (pos × p × 0.3)
        const debitCalcule = (pos * p * 60) / 200;
        setDebit(debitCalcule.toFixed(1));
      } else {
        setDebit(null);
      }
    } else {
      setDebit(null);
    }
  }, [poids, posologie]);

  const tableauDoses = [
    { posologie: '0.2', poids: [40, 50, 60, 70, 80, 90, 100], debits: [2.4, 3, 3.6, 4.2, 4.8, 5.4, 6] },
    { posologie: '0.4', poids: [40, 50, 60, 70, 80, 90, 100], debits: [4.8, 6, 7.2, 8.4, 9.6, 10.8, 12] },
    { posologie: '0.6', poids: [40, 50, 60, 70, 80, 90, 100], debits: [7.2, 9, 10.8, 12.6, 14.4, 16.2, 18] },
    { posologie: '0.8', poids: [40, 50, 60, 70, 80, 90, 100], debits: [9.6, 12, 14.4, 16.8, 19.2, 21.6, 24] },
    { posologie: '1', poids: [40, 50, 60, 70, 80, 90, 100], debits: [12, 15, 18, 21, 24, 27, 30] }
  ];

  const sectionsData = [
    {
      id: 'forme',
      number: '01',
      title: 'Forme et Présentation',
      icon: Syringe,
      color: 'text-cyan-400',
      content: {
        forme: 'Ampoule : 8 mg / 4 ml'
      }
    },
    {
      id: 'indications',
      number: '02',
      title: 'Indications',
      icon: Heart,
      color: 'text-red-400',
      items: [
        'Choc septique',
        'Choc hémorragique',
        'Intoxications aux inhibiteurs calciques, aux tricycliques',
        'Embolie pulmonaire grave',
        'Purpura fulminans',
        'Choc chez le traumatisé'
      ]
    },
    {
      id: 'contre-indications',
      number: '04',
      title: 'Contre-indications',
      icon: AlertTriangle,
      color: 'text-red-400',
      items: [
        { label: 'Hypertension artérielle (HTA)', severity: 'high' },
        { label: 'Myocardiopathie', severity: 'high' },
        { label: 'Troubles du rythme cardiaque', severity: 'high' },
        { label: 'Angor aigu', severity: 'high' },
        { label: 'Infarctus du myocarde récent', severity: 'critical' },
        { label: 'Grossesse', severity: 'high' }
      ]
    },
    {
      id: 'classe',
      number: '05',
      title: 'Classe Pharmacologique & Effets',
      icon: Info,
      color: 'text-purple-400',
      items: [
        'Sympathomimétique',
        'Alpha stimulant',
        'Vasoconstricteur',
        'Mydriatique',
        'Coronarodilatateur'
      ]
    },
    {
      id: 'metabolisme',
      number: '06',
      title: 'Métabolisme & Pharmacocinétique',
      icon: Activity,
      color: 'text-green-400',
      items: [
        'Désamination oxydative par la monoamine oxydase (MAO)',
        'O-méthylation par la catéchol-O-méthyltransférase (COMT)',
        'Élimination rénale'
      ]
    },
    {
      id: 'effets-indesirables',
      number: '07',
      title: 'Effets Indésirables',
      icon: AlertTriangle,
      color: 'text-amber-400',
      items: [
        'Hypertension artérielle (HTA)',
        'Tachycardie',
        'Bradycardie',
        'Anxiété',
        'Céphalées',
        'Tremblements',
        'Pâleur',
        'Gêne respiratoire',
        'Sueurs',
        'Précordialgies',
        'Crise de glaucome aigu'
      ]
    },
    {
      id: 'precautions',
      number: '08',
      title: 'Précautions & Surveillance',
      icon: Shield,
      color: 'text-blue-400',
      items: [
        { label: 'Administration strictement intraveineuse', critical: true },
        { label: 'Surveillance continue de la PA (PAM cible)', critical: true },
        { label: 'Surveillance ECG continue', critical: true },
        { label: 'Adapter la dose toutes les 5 minutes', critical: true },
        { label: 'Voie veineuse centrale recommandée', critical: true }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="noradrenaline-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Droplets className="w-10 h-10 text-red-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-red-400 to-red-300 bg-clip-text text-transparent">
              Noradrénaline
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm md:text-base max-w-3xl mx-auto">
            Guide complet : posologie, calcul automatique de dose et surveillance
          </p>
        </div>

        {/* Alerte */}
        <Alert className="bg-red-500/10 border-red-500/30">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <p className="font-semibold mb-1">⚠️ Médicament vasopresseur puissant</p>
            <p className="text-sm text-[hsl(var(--text-dim))]">
              Administration STRICTEMENT intraveineuse. Surveillance continue de la PA et de l'ECG obligatoire.
            </p>
          </AlertDescription>
        </Alert>

        {/* Calculateur de Dose */}
        <Card className="bg-[hsl(var(--bg-elev))] border-2 border-cyan-500/50" data-testid="calculateur-dose">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Calculator className="w-5 h-5" />
              03 - Posologie & Calculateur Automatique
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Type de patient */}
            <div>
              <Label className="text-sm text-[hsl(var(--text))] mb-2 block">Type de patient</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPatientType('adulte')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    patientType === 'adulte'
                      ? 'bg-cyan-500/20 border-cyan-500 shadow-lg'
                      : 'bg-[hsl(var(--bg))] border-[hsl(var(--border))] hover:border-cyan-500/30'
                  }`}
                  data-testid="patient-adulte"
                >
                  <Badge className={`${patientType === 'adulte' ? 'bg-cyan-500' : 'bg-gray-500'} text-white mb-1`}>
                    Adulte
                  </Badge>
                  <p className="text-xs text-[hsl(var(--text-dim))]">0,2 à 1 µg/kg/min</p>
                </button>
                <button
                  onClick={() => setPatientType('pediatrique')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    patientType === 'pediatrique'
                      ? 'bg-pink-500/20 border-pink-500 shadow-lg'
                      : 'bg-[hsl(var(--bg))] border-[hsl(var(--border))] hover:border-pink-500/30'
                  }`}
                  data-testid="patient-pediatrique"
                >
                  <Badge className={`${patientType === 'pediatrique' ? 'bg-pink-500' : 'bg-gray-500'} text-white mb-1`}>
                    Pédiatrique
                  </Badge>
                  <p className="text-xs text-[hsl(var(--text-dim))]">Débuter à 0,1 µg/kg/min</p>
                </button>
              </div>
            </div>

            {/* ADULTE */}
            {patientType === 'adulte' && (
              <>
                <Alert className="bg-cyan-500/10 border-cyan-500/30">
                  <Info className="w-4 h-4 text-cyan-400" />
                  <AlertDescription>
                    <p className="text-sm text-[hsl(var(--text))] font-semibold mb-2">Dilution ADULTE :</p>
                    <ul className="text-xs text-[hsl(var(--text-dim))] space-y-1">
                      <li>• 1 ampoule 4 ml (8 mg) + 36 ml G5%</li>
                      <li>• Soit 40 ml total → Concentration : 0,2 mg/ml</li>
                      <li>• Débit : 0,2 à 1 µg/kg/min en IVSE</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="poids" className="text-sm text-[hsl(var(--text))]">Poids du patient (kg)</Label>
                    <Input
                      id="poids"
                      type="number"
                      placeholder="Ex: 70"
                      value={poids}
                      onChange={(e) => setPoids(e.target.value)}
                      className="mt-1"
                      data-testid="input-poids"
                    />
                  </div>
                  <div>
                    <Label htmlFor="posologie" className="text-sm text-[hsl(var(--text))]">Posologie (µg/kg/min)</Label>
                    <Input
                      id="posologie"
                      type="number"
                      step="0.1"
                      min="0.2"
                      max="1"
                      placeholder="Ex: 0.5"
                      value={posologie}
                      onChange={(e) => setPosologie(e.target.value)}
                      className="mt-1"
                      data-testid="input-posologie"
                    />
                  </div>
                </div>

                {/* Résultat */}
                {debit && (
                  <div className="p-4 rounded-lg bg-green-500/10 border-2 border-green-500/50" data-testid="resultat-debit">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[hsl(var(--text-dim))] mb-1">Débit de perfusion :</p>
                        <p className="text-3xl font-bold text-green-300">{debit} ml/h</p>
                      </div>
                      <Activity className="w-12 h-12 text-green-400" />
                    </div>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2">
                      Pour {poids} kg à {posologie} µg/kg/min
                    </p>
                  </div>
                )}
              </>
            )}

            {/* PEDIATRIQUE */}
            {patientType === 'pediatrique' && (
              <>
                <Alert className="bg-pink-500/10 border-pink-500/30">
                  <Info className="w-4 h-4 text-pink-400" />
                  <AlertDescription>
                    <p className="text-sm text-[hsl(var(--text))] font-semibold mb-2">Dilution PÉDIATRIQUE :</p>
                    <ul className="text-xs text-[hsl(var(--text-dim))] space-y-1">
                      <li>• Diluer (0,3 × poids en kg) mg de noradrénaline</li>
                      <li>• Dans 50 ml de NaCl 0,9%</li>
                      <li>• Mettre le PSE à 1 ml/h = 0,1 µg/kg/min</li>
                      <li>• Augmentation progressive jusqu'à PAS cible</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                <div>
                  <Label htmlFor="poids-ped" className="text-sm text-[hsl(var(--text))]">Poids de l'enfant (kg)</Label>
                  <Input
                    id="poids-ped"
                    type="number"
                    placeholder="Ex: 20"
                    value={poids}
                    onChange={(e) => setPoids(e.target.value)}
                    className="mt-1"
                    data-testid="input-poids-ped"
                  />
                </div>

                {poids && (
                  <div className="p-4 rounded-lg bg-pink-500/10 border-2 border-pink-500/50">
                    <p className="text-sm text-[hsl(var(--text-dim))] mb-2">Préparation pour {poids} kg :</p>
                    <p className="text-lg font-bold text-pink-300 mb-2">
                      Prélever {(0.3 * parseFloat(poids)).toFixed(2)} mg de noradrénaline
                    </p>
                    <p className="text-sm text-[hsl(var(--text-dim))]">
                      (Soit {((0.3 * parseFloat(poids)) / 2).toFixed(2)} ml d'ampoule à 2 mg/ml)
                    </p>
                    <p className="text-xs text-[hsl(var(--text-dim))] mt-2">
                      Ramener à 50 ml avec NaCl 0,9%, débuter à 1 ml/h
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Tableau de référence ADULTE */}
            {patientType === 'adulte' && (
              <div className="mt-6">
                <h4 className="font-semibold text-[hsl(var(--text))] mb-3">Tableau de référence - Vitesse en ml/h</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[hsl(var(--border))]">
                        <th className="text-left p-2 font-semibold text-[hsl(var(--text))]">Posologie</th>
                        {tableauDoses[0].poids.map((p) => (
                          <th key={p} className="text-center p-2 font-semibold text-[hsl(var(--text))]">{p} kg</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableauDoses.map((row, idx) => (
                        <tr key={idx} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--bg))]">
                          <td className="p-2 text-[hsl(var(--text))]">{row.posologie} µg/kg/min</td>
                          {row.debits.map((d, i) => (
                            <td key={i} className="text-center p-2 text-[hsl(var(--text-dim))]">{d}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Accordéons pour les autres sections */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Info className="w-5 h-5" />
              Informations Complètes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="space-y-4">
              {sectionsData.map((section) => {
                const SectionIcon = section.icon;
                
                return (
                  <AccordionItem 
                    key={section.id} 
                    value={section.id}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                    data-testid={`section-${section.id}`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))] transition-colors">
                      <div className="flex items-center gap-3">
                        <Badge className="bg-cyan-500 text-white font-bold">{section.number}</Badge>
                        <SectionIcon className={`w-5 h-5 ${section.color}`} />
                        <span className="font-semibold text-[hsl(var(--text))]">{section.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="mt-3">
                        {/* Forme */}
                        {section.content && (
                          <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                            <p className="text-[hsl(var(--text))]">{section.content.forme}</p>
                          </div>
                        )}

                        {/* Listes */}
                        {section.items && (
                          <ul className="space-y-2">
                            {section.items.map((item, idx) => {
                              const isObject = typeof item === 'object';
                              const label = isObject ? item.label : item;
                              const severity = isObject ? item.severity : null;
                              const critical = isObject ? item.critical : false;
                              
                              return (
                                <li 
                                  key={idx}
                                  className={`flex items-start gap-2 p-3 rounded-lg ${
                                    severity === 'critical' ? 'bg-red-500/10 border border-red-500/30' :
                                    severity === 'high' ? 'bg-amber-500/10 border border-amber-500/30' :
                                    critical ? 'bg-amber-500/10 border border-amber-500/30' :
                                    'bg-[hsl(var(--bg-elev))]'
                                  }`}
                                >
                                  <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-cyan-500" />
                                  <span className="text-sm text-[hsl(var(--text-dim))]">{label}</span>
                                  {(severity === 'critical' || critical) && (
                                    <Badge variant="outline" className="ml-auto border-red-500/30 text-red-300 text-xs">
                                      Critique
                                    </Badge>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
